export default class SliderTeam {
  constructor(el) {
    this.el = el
    this.track = el.querySelector('.slider-team--track')
    this.originalSlides = [...this.track.children]
    this.prevBtns = [...el.querySelectorAll('.slider-team--arrow.prev')]
    this.nextBtns = [...el.querySelectorAll('.slider-team--arrow.next')]

    // Vitesse du défilement automatique en px par frame (~60px/s à 60fps)
    this.speed = 1

    // Position courante du track en px (desktop autoplay)
    this.x = 0

    this.isDragging = false
    this.startX = 0
    this.dragX = 0

    // Pause du défilement au survol
    this.isPaused = false

    // Largeur totale d'un set de slides (sans les clones) — seuil de reset
    this.trackWidth = 0

    // Index du slide actif (mobile uniquement)
    this.current = 0

    this.init()
  }

  // En dessous de 992px on bascule en mode navigation manuelle
  get isMobile() {
    return window.innerWidth < 992
  }

  init() {
    // On duplique tous les slides originaux à la suite du track.
    // Quand le défilement atteint la fin du premier set, on repart
    // de zéro sans saut visible grâce au reset de this.x.
    // data-index permet aux clones de retrouver leur slide original au clic.
    this.originalSlides.forEach((slide, i) => {
      slide.dataset.index = i
      const clone = slide.cloneNode(true)
      clone.setAttribute('aria-hidden', 'true')
      this.track.appendChild(clone)
    })

    // Pause/reprise au survol du bloc entier
    this.el.addEventListener('mouseenter', () => { this.isPaused = true })
    this.el.addEventListener('mouseleave', () => { this.isPaused = false })

    // Drag desktop
    this.track.addEventListener('mousedown', e => this.onDragStart(e))
    this.track.addEventListener('touchstart', e => this.onDragStart(e), { passive: true })
    window.addEventListener('mouseup', () => this.onDragEnd())
    window.addEventListener('touchend', () => this.onDragEnd())
    window.addEventListener('mousemove', e => this.onDragMove(e))
    window.addEventListener('touchmove', e => this.onDragMove(e), { passive: true })

    // Flèches mobile
    this.prevBtns.forEach(btn => btn.addEventListener('click', () => this.navigate(-1)))
    this.nextBtns.forEach(btn => btn.addEventListener('click', () => this.navigate(1)))

    // Clic sur un slide en mobile → colle le slide sur le bord gauche + ouvre l'overlay
    // Géré sur tous les slides (originaux + clones) via data-index
    // stopPropagation empêche le listener document de fermer l'overlay immédiatement
    this.track.querySelectorAll('.slider-team--slide').forEach(slide => {
      slide.addEventListener('click', e => {
        e.stopPropagation()
        const i = parseInt(slide.dataset.index)
        const original = this.originalSlides[i]
        const allSlides = [...this.track.querySelectorAll('.slider-team--slide')]

        if (this.isMobile) {
          // Snap : positionne le slide original à gauche du slider
          this.current = i
          this.track.style.transition = 'transform .4s ease-in-out'
          this.track.style.transform = `translateX(-${original.offsetLeft}px)`
          setTimeout(() => { this.track.style.transition = '' }, 400)
          const wasActive = original.classList.contains('is-active')
          allSlides.forEach(s => s.classList.remove('is-active'))
          if (!wasActive) original.classList.add('is-active')
        } else {
          // Desktop : is-active sur le slide cliqué (peut être un clone)
          const wasActive = slide.classList.contains('is-active')
          allSlides.forEach(s => s.classList.remove('is-active'))
          if (!wasActive) {
            slide.classList.add('is-active')
            this.isPaused = true
          } else {
            this.isPaused = false
          }
        }
      })
    })

    // Clic en dehors du slider → ferme tous les overlays et reprend le défilement
    document.addEventListener('click', () => {
      this.track.querySelectorAll('.slider-team--slide').forEach(s => s.classList.remove('is-active'))
      this.isPaused = false
    })

    // Reset de position si on change d'orientation ou de taille
    window.addEventListener('resize', () => this.onResize())

    // On attend que les images soient chargées pour avoir les bonnes dimensions
    const start = () => {
      this.trackWidth = this.originalSlides.reduce((acc, s) => acc + s.offsetWidth, 0)
      this.tick()
    }

    document.readyState === 'complete' ? start() : window.addEventListener('load', start)
  }

  // Navigation par flèches (mobile) — déplace vers le slide index this.current
  navigate(dir) {
    this.current = Math.max(0, Math.min(this.current + dir, this.originalSlides.length - 1))
    const offset = this.originalSlides[this.current].offsetLeft
    this.track.style.transition = 'transform .4s ease-in-out'
    this.track.style.transform = `translateX(-${offset}px)`
    // On retire la transition après l'animation pour ne pas bloquer le rAF desktop
    setTimeout(() => { this.track.style.transition = '' }, 400)
  }

  // Boucle principale — tourne à chaque frame via rAF
  // En mode desktop : déplace le track et remet à zéro quand on a parcouru un set complet
  // En mode mobile : ne touche pas au transform (géré par navigate())
  tick() {
    if (!this.isMobile) {
      if (!this.isPaused && !this.isDragging) {
        this.x -= this.speed
        // Reset dès qu'on a scrollé d'une largeur de set entier → boucle seamless
        if (Math.abs(this.x) >= this.trackWidth) this.x += this.trackWidth
      }
      this.track.style.transform = `translateX(${this.x}px)`
    }
    requestAnimationFrame(() => this.tick())
  }

  // Synchronise la position quand le viewport change de mode (mobile ↔ desktop)
  onResize() {
    if (this.isMobile) {
      this.current = 0
      this.track.style.transform = 'translateX(0)'
    } else {
      this.x = 0
    }
  }

  onDragStart(e) {
    if (this.isMobile) return
    this.isDragging = true
    this.startX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX
    // Sauvegarde la position au moment du clic pour calculer le delta
    this.dragX = this.x
  }

  onDragMove(e) {
    if (!this.isDragging) return
    const currentX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX
    this.x = this.dragX + (currentX - this.startX)
    // Normalisation pour rester dans les bornes du set (évite de sortir du loop)
    if (this.x > 0) this.x -= this.trackWidth
    if (Math.abs(this.x) >= this.trackWidth) this.x += this.trackWidth
  }

  onDragEnd() {
    this.isDragging = false
  }
}
